import React, { useState, useMemo } from 'react';
import { useI18n } from '../i18n/context';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from './ui/button';
import AvatarPickerModal from './AvatarPickerModal';
import { Card, CardContent, CardHeader, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { PasswordInput } from './ui/password-input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { GoogleLogin } from '@react-oauth/google';
import AppleSignInButton from './AppleSignInButton';
import { buildApiUrl } from '../config/api';
import { generateUniqueRandomNickname } from '../utils/uniqueNickname';
import { SparklesCore } from './ui/sparkles';
import { cn } from '../lib/utils';
import { isCapacitorNative, nativeGoogleSignIn } from '../lib/nativeGoogleAuth';

const GOOGLE_WEB_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
/** Кнопка Google: задайте REACT_APP_GOOGLE_CLIENT_ID (тот же Client ID, что GOOGLE_CLIENT_ID на сервере) */
const GOOGLE_AUTH_ENABLED = !!GOOGLE_WEB_CLIENT_ID;
const IS_NATIVE = isCapacitorNative();
/** Краткое уведомление после входа — не перекрывает личный кабинет надолго. */
const AUTH_SUCCESS_TOAST_MS = 1000;

const GoogleIcon = ({ className = '' }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

/** Sign in with Apple: Services ID — REACT_APP_APPLE_CLIENT_ID, на сервере APPLE_CLIENT_ID; Return URL в консоли Apple = origin или REACT_APP_APPLE_REDIRECT_URI */
const APPLE_CLIENT_ID = (process.env.REACT_APP_APPLE_CLIENT_ID || '').trim();
const APPLE_REDIRECT_URI = (process.env.REACT_APP_APPLE_REDIRECT_URI || '').trim();
const APPLE_AUTH_ENABLED = !!APPLE_CLIENT_ID;

const UserSelector = ({ onUserSelect }) => {
  const { t, locale } = useI18n();
  const loginSchema = useMemo(
    () =>
      z.object({
        email: z.string().min(1, t('auth.validation.emailRequired')).email(t('auth.validation.emailInvalid')),
        password: z.string().min(1, t('auth.validation.passwordRequired')),
      }),
    [t]
  );
  const registerSchema = useMemo(
    () =>
      z
        .object({
          name: z.string().min(2, t('auth.validation.nameMin')),
          email: z.string().min(1, t('auth.validation.emailRequired')).email(t('auth.validation.emailInvalid')),
          password: z.string().min(8, t('auth.validation.passwordMin')),
          confirmPassword: z.string().min(1, t('auth.validation.confirmRequired')),
          agreement: z.literal(true, {
            errorMap: () => ({ message: t('auth.validation.acceptTerms') }),
          }),
        })
        .refine((data) => data.password === data.confirmPassword, {
          message: t('auth.validation.passwordsMismatch'),
          path: ['confirmPassword'],
        }),
    [t]
  );

  const loginResolver = useMemo(() => zodResolver(loginSchema), [loginSchema]);
  const registerResolver = useMemo(() => zodResolver(registerSchema), [registerSchema]);

  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [showExistsDialog, setShowExistsDialog] = useState(false);
  const [existingEmail, setExistingEmail] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [registerAvatar, setRegisterAvatar] = useState('');
  // Форма входа
  const loginForm = useForm({
    resolver: loginResolver,
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onBlur',
  });

  // Форма регистрации
  const registerForm = useForm({
    resolver: registerResolver,
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      agreement: false,
    },
    mode: 'onBlur',
  });

  // Обработка входа
  const handleLogin = async (data) => {
    setIsLoading(true);
    try {
      const response = await fetch(buildApiUrl('/users/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          nickname: data.email, 
          password: data.password 
        }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(t('auth.loginSuccess'), { duration: AUTH_SUCCESS_TOAST_MS });
        onUserSelect(result);
      } else {
        if (result.error?.includes('not found') || result.error?.includes('не найден')) {
          toast.error(t('auth.userNotFound'));
        } else if (result.error?.includes('password') || result.error?.includes('пароль')) {
          toast.error(t('auth.wrongPassword'));
        } else {
          toast.error(result.error || t('auth.loginFailed'));
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(t('auth.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Обработка регистрации
  const handleRegister = async (data) => {
    setIsLoading(true);
    try {
      let nickname;
      try {
        nickname = await generateUniqueRandomNickname();
      } catch (e) {
        console.error(e);
        toast.error(e?.message || t('auth.registerPrepFail'));
        setIsLoading(false);
        return;
      }
      const response = await fetch(buildApiUrl('/users'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: data.name,
          nickname,
          email: data.email,
          password: data.password,
          avatar: registerAvatar && registerAvatar.trim() ? registerAvatar.trim() : null,
          // пока без выбора роли: по дефолту Тренер
          is_trainer: true,
          is_client: false,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(t('auth.registerSuccess'), { duration: AUTH_SUCCESS_TOAST_MS });
        onUserSelect(result);
      } else {
        if (result.error?.toLowerCase?.().includes('nickname')) {
          toast.error(t('auth.nicknameTaken'));
          return;
        }
        if (result.error?.includes('already exists') || result.error?.includes('уже существует')) {
          setExistingEmail(data.email);
          setShowExistsDialog(true);
        } else {
          toast.error(result.error || t('auth.registerFailed'));
        }
      }
    } catch (error) {
      console.error('Register error:', error);
      toast.error(t('auth.serverError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNativeGoogle = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const idToken = await nativeGoogleSignIn(GOOGLE_WEB_CLIENT_ID);
      await handleGoogleCredential({ credential: idToken });
    } catch (err) {
      console.error('Native Google sign-in error:', err);
      const msg = String(err?.message || err?.code || err || 'unknown error');
      if (/cancel/i.test(msg)) {
        // Пользователь закрыл — молча
      } else {
        toast.error(`${t('auth.googleOpenFail')}: ${msg}`, { duration: 12000 });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = async (credentialResponse) => {
    const credential = credentialResponse?.credential;
    if (!credential) {
      toast.error(t('auth.googleNoData'));
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(buildApiUrl('/auth/google'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential }),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(t('auth.googleSuccess'), { duration: AUTH_SUCCESS_TOAST_MS });
        onUserSelect(result);
      } else {
        toast.error(result.error || t('auth.googleError'));
      }
    } catch (error) {
      console.error('Google login error:', error);
      toast.error(t('auth.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleCredential = async ({ identityToken, appleUser }) => {
    if (!identityToken) {
      toast.error(t('auth.appleNoData'));
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(buildApiUrl('/auth/apple'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ identityToken, appleUser: appleUser || null }),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(t('auth.appleSuccess'), { duration: AUTH_SUCCESS_TOAST_MS });
        onUserSelect(result);
      } else {
        toast.error(result.error || t('auth.appleError'));
      }
    } catch (error) {
      console.error('Apple login error:', error);
      toast.error(t('auth.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    const fromForm = loginForm.getValues('email');
    setForgotEmail(fromForm && String(fromForm).trim() ? String(fromForm).trim() : '');
    setForgotOpen(true);
  };

  const submitForgotPassword = async () => {
    const email = String(forgotEmail || '').trim().toLowerCase();
    if (!email) {
      toast.error(t('auth.enterEmail'));
      return;
    }
    setForgotSubmitting(true);
    const controller = new AbortController();
    const abortAfterMs = 45000;
    const timer = setTimeout(() => controller.abort(), abortAfterMs);
    try {
      const response = await fetch(buildApiUrl('/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
        signal: controller.signal,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = result?.error && String(result.error).trim() ? String(result.error).trim() : '';
        toast.error(detail || t('auth.resetSendFail'));
        if (detail) console.warn('[forgot-password]', detail);
        return;
      }
      toast.success(t('auth.resetCheckEmail'));
      setForgotOpen(false);
    } catch (err) {
      console.error(err);
      if (err?.name === 'AbortError') {
        toast.error(t('auth.resetTimeout'));
      } else {
        toast.error(t('auth.resetNetwork'));
      }
    } finally {
      clearTimeout(timer);
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] min-h-screen w-full max-w-[100vw] min-w-0 overflow-hidden bg-black">
      {/* Космический фон как на первом экране */}
      <SparklesCore
        className="absolute inset-0 h-full w-full"
        background="transparent"
        particleColor="#ffffff"
        particleDensity={110}
        minSize={0.6}
        maxSize={1.4}
        speed={1}
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.05)_0%,rgba(0,0,0,0.75)_70%,rgba(0,0,0,1)_100%)]" />

      <div className="relative z-10 flex min-h-[100dvh] min-h-screen w-full min-w-0 max-w-full items-center justify-center p-3 sm:p-4">
        <Card className="w-full min-w-0 max-w-md rounded-2xl shadow-2xl bg-white">
        <CardHeader className="pb-0 pt-6 px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-100/80 p-1 rounded-lg">
              <TabsTrigger 
                value="login" 
                className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium transition-all"
              >
                {t('auth.tabLogin')}
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm font-medium transition-all"
              >
                {t('auth.tabRegister')}
              </TabsTrigger>
            </TabsList>
            
            <CardContent className="px-0 pt-6 pb-6">
              {/* Форма входа */}
              <TabsContent value="login" className="mt-0 space-y-4">
                <CardDescription className="text-gray-600 text-sm">
                  {t('auth.loginSubtitle')}
                </CardDescription>
                
                <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-sm font-medium text-gray-900">
                      {t('auth.email')}
                    </Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      className={`h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 ${loginForm.formState.errors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      {...loginForm.register('email')}
                    />
                    {loginForm.formState.errors.email && (
                      <p className="text-sm text-red-500">{loginForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Label htmlFor="login-password" className="text-sm font-medium text-gray-900">
                        {t('auth.password')}
                      </Label>
                      <a
                        href="#"
                        onClick={handleForgotPassword}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                      >
                        {t('auth.forgotPassword')}
                      </a>
                    </div>
                    <PasswordInput
                      id="login-password"
                      placeholder="••••••••"
                      maskResetKey={activeTab}
                      showPasswordLabel={t('auth.showPassword')}
                      hidePasswordLabel={t('auth.hidePassword')}
                      className={`h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 ${loginForm.formState.errors.password ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      {...loginForm.register('password')}
                    />
                    {loginForm.formState.errors.password && (
                      <p className="text-sm text-red-500">{loginForm.formState.errors.password.message}</p>
                    )}
                  </div>
              
                  <Button 
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-11 bg-black text-white hover:bg-gray-800 rounded-lg text-sm font-medium transition-all"
                  >
                    {isLoading ? t('auth.loginLoading') : t('auth.loginSubmit')}
                  </Button>
                  
                  {GOOGLE_AUTH_ENABLED || APPLE_AUTH_ENABLED ? (
                    <>
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-gray-400">{t('auth.or')}</span>
                        </div>
                      </div>

                      <div className="flex w-full flex-col items-center gap-3">
                        {GOOGLE_AUTH_ENABLED ? (
                          <div
                            className={cn(
                              'flex w-full justify-center min-h-[44px]',
                              isLoading && 'pointer-events-none opacity-60'
                            )}
                          >
                            {IS_NATIVE ? (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleNativeGoogle}
                                disabled={isLoading}
                                className="h-11 w-full max-w-[320px] border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                              >
                                <GoogleIcon className="mr-2 h-5 w-5" />
                                {t('auth.googleContinue')}
                              </Button>
                            ) : (
                              <GoogleLogin
                                onSuccess={handleGoogleCredential}
                                onError={() => toast.error(t('auth.googleOpenFail'))}
                                useOneTap={false}
                                use_fedcm_for_button={false}
                                theme="outline"
                                size="large"
                                text="continue_with"
                                shape="rectangular"
                                locale={locale === 'en' ? 'en' : locale === 'es' ? 'es' : 'ru'}
                                width={320}
                              />
                            )}
                          </div>
                        ) : null}
                        {APPLE_AUTH_ENABLED ? (
                          <div
                            className={cn(
                              'flex w-full flex-col items-center',
                              isLoading && 'pointer-events-none opacity-60'
                            )}
                          >
                            <AppleSignInButton
                              clientId={APPLE_CLIENT_ID}
                              redirectURI={APPLE_REDIRECT_URI || undefined}
                              label={t('auth.appleContinue')}
                              disabled={isLoading}
                              onSuccess={handleAppleCredential}
                              onError={() => toast.error(t('auth.appleError'))}
                            />
                          </div>
                        ) : null}
                      </div>
                    </>
                  ) : null}
                </form>
              </TabsContent>
              
              {/* Форма регистрации */}
              <TabsContent value="register" className="mt-0 space-y-4">
                <CardDescription className="text-gray-600 text-sm">
                  {t('auth.registerSubtitle')}
                </CardDescription>
                
          <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center">
                {registerAvatar ? (
                  <img src={registerAvatar} alt="avatar" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-white/70 text-sm">
                    {(registerForm.watch('name') || '?').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <Button type="button" variant="outline" onClick={() => setShowAvatarPicker(true)}>
                {t('auth.pickAvatar')}
              </Button>
            </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-sm font-medium text-gray-900">
                      {t('auth.name')}
                    </Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder={t('auth.namePlaceholder')}
                      className={`h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 ${registerForm.formState.errors.name ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      {...registerForm.register('name')}
                    />
                    {registerForm.formState.errors.name && (
                      <p className="text-sm text-red-500">{registerForm.formState.errors.name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-sm font-medium text-gray-900">
                      {t('auth.email')}
                    </Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      className={`h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 ${registerForm.formState.errors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      {...registerForm.register('email')}
                    />
                    {registerForm.formState.errors.email && (
                      <p className="text-sm text-red-500">{registerForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-sm font-medium text-gray-900">
                      {t('auth.password')}
                    </Label>
                    <PasswordInput
                      id="register-password"
                      placeholder={t('auth.passwordMinPlaceholder')}
                      maskResetKey={activeTab}
                      showPasswordLabel={t('auth.showPassword')}
                      hidePasswordLabel={t('auth.hidePassword')}
                      className={`h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 ${registerForm.formState.errors.password ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      {...registerForm.register('password')}
                    />
                    {registerForm.formState.errors.password && (
                      <p className="text-sm text-red-500">{registerForm.formState.errors.password.message}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password" className="text-sm font-medium text-gray-900">
                      {t('auth.confirmPassword')}
                    </Label>
                    <PasswordInput
                      id="register-confirm-password"
                      placeholder={t('auth.confirmPassword')}
                      maskResetKey={activeTab}
                      showPasswordLabel={t('auth.showPassword')}
                      hidePasswordLabel={t('auth.hidePassword')}
                      className={`h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 ${registerForm.formState.errors.confirmPassword ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      {...registerForm.register('confirmPassword')}
                    />
                    {registerForm.formState.errors.confirmPassword && (
                      <p className="text-sm text-red-500">{registerForm.formState.errors.confirmPassword.message}</p>
                    )}
                  </div>
                  
                  
                  
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="agreement"
                      checked={registerForm.watch('agreement')}
                      onCheckedChange={(checked) =>
                        registerForm.setValue('agreement', checked === true, { shouldValidate: true })
                      }
                      className={registerForm.formState.errors.agreement ? 'border-red-500' : ''}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label
                        htmlFor="agreement"
                        className="text-sm font-normal leading-relaxed cursor-pointer text-gray-700"
                      >
                        {t('auth.agreePrefix')}{' '}
                        <a
                          href="/terms"
                          className="text-blue-600 hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.dispatchEvent(new CustomEvent('mad:openTerms'));
                          }}
                        >
                          {t('auth.termsLink')}
                        </a>{' '}
                        {t('auth.andWith')}{' '}
                        <a
                          href="/privacy"
                          className="text-blue-600 hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.dispatchEvent(new CustomEvent('mad:openPrivacy'));
                          }}
                        >
                          {t('auth.privacyLink')}
                        </a>
                      </Label>
                      {registerForm.formState.errors.agreement && (
                        <p className="text-sm text-red-500">{registerForm.formState.errors.agreement.message}</p>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    type="submit"
                    disabled={isLoading || !registerForm.watch('agreement')}
                    className="w-full h-11 bg-black text-white hover:bg-gray-800 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                  >
                    {isLoading ? t('auth.registerLoading') : t('auth.registerSubmit')}
                  </Button>
                  
                  {GOOGLE_AUTH_ENABLED ? (
                    <>
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-gray-400">{t('auth.or')}</span>
                        </div>
                      </div>
                      
                      <div
                        className={cn(
                          'flex w-full justify-center min-h-[44px]',
                          isLoading && 'pointer-events-none opacity-60'
                        )}
                      >
                        {IS_NATIVE ? (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleNativeGoogle}
                            disabled={isLoading}
                            className="h-11 w-full max-w-[320px] border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
                          >
                            <GoogleIcon className="mr-2 h-5 w-5" />
                            {t('auth.googleSignup')}
                          </Button>
                        ) : (
                          <GoogleLogin
                            onSuccess={handleGoogleCredential}
                            onError={() => toast.error(t('auth.googleOpenFail'))}
                            useOneTap={false}
                            use_fedcm_for_button={false}
                            theme="outline"
                            size="large"
                            text="signup_with"
                            shape="rectangular"
                            locale={locale === 'en' ? 'en' : locale === 'es' ? 'es' : 'ru'}
                            width={320}
                          />
                        )}
                      </div>
                    </>
                  ) : null}
                </form>

          <AvatarPickerModal
            open={showAvatarPicker}
            onOpenChange={setShowAvatarPicker}
            title={t('tg.avatarPickerTitle')}
            selectedUrl={registerAvatar}
            onSelect={(url) => setRegisterAvatar(url)}
          />
              </TabsContent>
            </CardContent>
          </Tabs>
        </CardHeader>
      </Card>
      </div>

      {/* Диалог: пользователь уже существует */}
      <AlertDialog open={showExistsDialog} onOpenChange={setShowExistsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('auth.existsTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('auth.existsBody', { email: existingEmail })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
            <AlertDialogCancel className="mt-0">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowExistsDialog(false);
                setForgotEmail(existingEmail);
                setForgotOpen(true);
              }}
            >
              {t('auth.resetPasswordBtn')}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => {
                setShowExistsDialog(false);
                setActiveTab('login');
                loginForm.setValue('email', existingEmail);
              }}
            >
              {t('auth.loginSubmit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md bg-white text-gray-900">
          <DialogHeader>
            <DialogTitle>{t('auth.forgotDialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="forgot-email">{t('auth.email')}</Label>
            <Input
              id="forgot-email"
              type="email"
              className="h-11 bg-white border-gray-300"
              placeholder="email@example.com"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              disabled={forgotSubmitting}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setForgotOpen(false)} disabled={forgotSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={submitForgotPassword} disabled={forgotSubmitting}>
              {forgotSubmitting ? t('auth.forgotSending') : t('auth.forgotSendLink')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserSelector;
